// ============================================================================
// Feuille du noyau : LIMITES (refonte, lot 6.58, moteur v54).
// ============================================================================
// L'énoncé : une expression, une variable (x, ou n pour une suite) et un point
// (« x → +∞ », « x → 2⁻ »). L'élève écrit, une étape par ligne :
//
//   « x² → +∞ »                         une limite (morceau ou expression)
//   « x³ − 4x² + 1 = x³(1 − 4 ÷ x + 1 ÷ x³) »   une écriture égale près du point
//   « (x² − 4) ÷ (x − 2) = x + 2 → 4 »  les deux à la suite
//   « −1 ÷ n ≤ cos(n) ÷ n ≤ 1 ÷ n »     un encadrement (gendarmes, comparaison)
//
//   Limite de référence (x, xⁿ, 1 ÷ x, √x, eˣ, ln x, qⁿ)   1   limit.reference@1
//   Par continuité (en un réel)                           1   limit.continuity@1
//   Somme, produit, quotient, composée, puissance         1   limit.sum@1 …
//   Factoriser par le terme dominant                      2   limit.factor_dominant@1
//   Simplifier la fraction                                2   limit.simplify@1
//   Multiplier par l'expression conjuguée                 2   limit.conjugate@1
//   Transformer l'écriture (la forme n'est plus indéterminée) 2 limit.rewrite@1
//   Factoriser (un facteur commun apparaît)               1   limit.factor@1
//   Croissances comparées                                 2   limit.compare@1
//   Nombre dérivé                                         2   limit.derivative@1
//   Encadrer                                              1   limit.bound@1
//   Théorème des gendarmes / de comparaison               2   limit.squeeze@1 …
//
// Moteur v56 (lot 6.61) :
//   Nombre dérivé / croissances comparées, par composition 2   limit.derivative_composite@1 …
//   (sin(1 ÷ n) ÷ (1 ÷ n) : X = 1 ÷ n → 0 et sin(X) ÷ X → 1)
//   Écrire avec l'exponentielle (uᵛ = e^(v ln u))         1   limit.exp_form@1
//   arctan en radians (arctan(x) → π ÷ 2 en +∞) ; sin(1 ÷ x) reste borné (gendarmes).
//
// Moteur v58 (lot 6.63) : les DÉVELOPPEMENTS LIMITÉS en 0 (kernel-limit-dl.ts) :
//   « e^x = 1 + x + x² ÷ 2 + o(x²) »          DL de référence       2   limit.dl_reference@1
//   Composer les DL (eˣ², ln(cos x))          2   limit.dl_compose@1
//   Somme de DL, multiplier par xᵏ            1   limit.dl_sum@1 …
//   Produit, quotient de DL                   2   limit.dl_product@1 …
//   Diviser par xᵏ (o(x²) ÷ x² = o(1))        1   limit.dl_divide@1
//   Lire la limite sur le DL (o(1) → 0)       1   limit.dl_limit@1
//   Réduire au même dénominateur (∞ − ∞)      1   limit.same_denominator@1
// Seulement sur une feuille dont le corrigé écrit un « o(…) ».
//
// Les morceaux « immédiats » (une constante, une limite de référence, une seule opération
// entre une constante et une référence : « 3 ÷ x », « x − 2 ») n'ont pas besoin d'une
// ligne. Une forme indéterminée ne se conclut jamais par les opérations : une limite juste
// écrite d'un bond reste sur la feuille, sans HORA (D7). Une ligne fausse ne dit jamais la
// bonne limite.
// ============================================================================

import type { Chip } from './lexer';
import { checkSyntax, isCmp, lex, normalize, normalizeChips } from './lexer';
import type { ScratchModel } from './model';
import { atomsText, type Line } from './tree';
import { canon, parseStatement, termKey, type Term } from './kernel/term';
import {
  agreeTo,
  hasO,
  oAtomOrder,
  oText,
  serLimit,
  seriesOf,
  seriesTo,
  type Ser,
} from './kernel/series';
import {
  beyondOrder,
  degreeName,
  dlDerive,
  maxOOrder,
  oAtomsToSyms,
  restoreOAtoms,
  stepsDiff,
  textHasO,
  withOPlaceholders,
  type DLStep,
} from './kernel-limit-dl';
import {
  addL,
  constValue,
  continuousAt,
  divL,
  fin,
  fnL,
  hasVar,
  holdsNear,
  isDet,
  knownLimit,
  limValText,
  mulL,
  parsePoint,
  powL,
  referenceLimit,
  sameLimit,
  sameNear,
  theoremLimit,
  toRatFnSafe,
  trueLimit,
  type LimPoint,
  type LimRes,
  type LimVal,
} from './kernel/limit';
import {
  classifyEquiv,
  EQUIV,
  equivDiff,
  equivHolds,
  equivRatio,
  isZeroConst,
  type EquivStep,
} from './kernel-limit-equiv';

export const LIMIT_STEP_IDS = {
  reference: 'limit.reference@1',
  continuity: 'limit.continuity@1',
  sum: 'limit.sum@1',
  product: 'limit.product@1',
  quotient: 'limit.quotient@1',
  composition: 'limit.composition@1',
  power: 'limit.power@1',
  factorDominant: 'limit.factor_dominant@1',
  simplify: 'limit.simplify@1',
  conjugate: 'limit.conjugate@1',
  rewrite: 'limit.rewrite@1',
  factor: 'limit.factor@1',
  conjugateMul: 'limit.conjugate_mul@1',
  compare: 'limit.compare@1',
  derivative: 'limit.derivative@1',
  bound: 'limit.bound@1',
  squeeze: 'limit.squeeze@1',
  comparison: 'limit.comparison@1',
  derivativeComposite: 'limit.derivative_composite@1',
  compareComposite: 'limit.compare_composite@1',
  expForm: 'limit.exp_form@1',
  dlReference: 'limit.dl_reference@1',
  dlCompose: 'limit.dl_compose@1',
  dlSum: 'limit.dl_sum@1',
  dlScale: 'limit.dl_scale@1',
  dlProduct: 'limit.dl_product@1',
  dlDivide: 'limit.dl_divide@1',
  dlQuotient: 'limit.dl_quotient@1',
  dlLimit: 'limit.dl_limit@1',
  sameDenominator: 'limit.same_denominator@1',
  equivReference: 'limit.equiv_reference@1',
  equivDL: 'limit.equiv_dl@1',
  equivDominant: 'limit.equiv_dominant@1',
  equivReplace: 'limit.equiv_replace@1',
  lnRule: 'limit.ln_rule@1',
  reduce: 'limit.reduce@1',
} as const;
type StepKind = keyof typeof LIMIT_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  reference: {
    label: 'Limite de référence',
    hora: 1,
    why: 'Une limite du cours : x, xⁿ, 1 ÷ x, √x, eˣ, ln x, qⁿ.',
  },
  continuity: {
    label: 'Par continuité',
    hora: 1,
    why: 'L’expression est définie et continue au point : sa limite est sa valeur.',
  },
  sum: {
    label: 'Limite d’une somme',
    hora: 1,
    why: 'On additionne les limites des termes (ce n’est pas la forme ∞ − ∞).',
  },
  product: {
    label: 'Limite d’un produit',
    hora: 1,
    why: 'On multiplie les limites, avec la règle des signes (ce n’est pas la forme 0 × ∞).',
  },
  quotient: {
    label: 'Limite d’un quotient',
    hora: 1,
    why: 'On divise les limites ; un dénominateur qui tend vers 0 : son signe (0⁺ ou 0⁻) décide.',
  },
  composition: {
    label: 'Limite d’une composée',
    hora: 1,
    why: 'u tend vers b, et f(X) tend vers ℓ quand X tend vers b : f(u) tend vers ℓ.',
  },
  power: {
    label: 'Limite d’une puissance',
    hora: 1,
    why: 'Une puissance de ce qui a une limite, ou qⁿ selon q.',
  },
  factorDominant: {
    label: 'Factoriser par le terme dominant',
    hora: 2,
    why: 'On met en facteur le terme qui l’emporte : la forme n’est plus indéterminée.',
  },
  simplify: {
    label: 'Simplifier la fraction',
    hora: 2,
    why: 'On simplifie par le facteur qui s’annule : la forme n’est plus indéterminée.',
  },
  conjugate: {
    label: 'Multiplier par l’expression conjuguée',
    hora: 2,
    why: '(√a − √b)(√a + √b) = a − b : la différence de racines disparaît.',
  },
  rewrite: {
    label: 'Transformer l’écriture',
    hora: 2,
    why: 'Une écriture égale près du point, qui n’est plus une forme indéterminée.',
  },
  conjugateMul: {
    label: 'Multiplier par l’expression conjuguée',
    hora: 1,
    why: 'On multiplie en haut et en bas par l’expression conjuguée (√a + √b) : la valeur ne change pas.',
  },
  factor: {
    label: 'Factoriser',
    hora: 1,
    why: 'Un facteur commun apparaît en haut et en bas : il va se simplifier.',
  },
  compare: {
    label: 'Croissances comparées',
    hora: 2,
    why: 'En +∞, eˣ l’emporte sur toute puissance de x, qui l’emporte sur ln x ; en 0, xᵃ l’emporte sur ln x.',
  },
  derivative: {
    label: 'Nombre dérivé',
    hora: 2,
    why: '(f(x) − f(a)) ÷ (x − a) tend vers f′(a) : on dérive f, puis on prend sa valeur en a.',
  },
  bound: {
    label: 'Encadrer',
    hora: 1,
    why: 'On encadre l’expression près du point (−1 ≤ cos ≤ 1, …).',
  },
  squeeze: {
    label: 'Théorème des gendarmes',
    hora: 2,
    why: 'Encadrée par deux expressions qui ont la même limite, l’expression a cette limite.',
  },
  comparison: {
    label: 'Théorème de comparaison',
    hora: 2,
    why: 'Plus grande qu’une expression qui tend vers +∞, elle tend vers +∞ (plus petite qu’une qui tend vers −∞, vers −∞).',
  },
  derivativeComposite: {
    label: 'Nombre dérivé, par composition',
    hora: 2,
    why: 'On pose X égal au morceau intérieur : X tend vers a, et (f(X) − f(a)) ÷ (X − a) tend vers f′(a) (sin(X) ÷ X → 1 en 0). Par composition, l’expression a la même limite.',
  },
  compareComposite: {
    label: 'Croissances comparées, par composition',
    hora: 2,
    why: 'On pose X égal au morceau intérieur : X tend vers +∞ (ou 0), et les croissances comparées donnent la limite en X. Par composition, l’expression a la même limite.',
  },
  expForm: {
    label: 'Écrire avec l’exponentielle',
    hora: 1,
    why: 'uᵛ = e^(v × ln(u)) (u > 0) : la puissance devient une exponentielle, on cherche la limite de v × ln(u).',
  },
  dlReference: {
    label: 'DL de référence',
    hora: 2,
    why: 'Un développement limité du cours en 0 (eˣ, sin x, cos x, ln(1 + x), (1 + x)^a…), écrit jusqu’à l’ordre voulu.',
  },
  dlCompose: {
    label: 'Composer les DL',
    hora: 2,
    why: 'Dans un DL de référence, on remplace la variable par une expression qui tend vers 0, et on garde les termes jusqu’à l’ordre voulu.',
  },
  dlSum: {
    label: 'Somme de DL',
    hora: 1,
    why: 'On additionne terme à terme ; le reste est le plus gros des o(…).',
  },
  dlScale: {
    label: 'Multiplier un DL',
    hora: 1,
    why: 'Multiplier par une puissance de x décale tous les degrés, le reste aussi : x × o(x²) = o(x³).',
  },
  dlProduct: {
    label: 'Produit de DL',
    hora: 2,
    why: 'On développe le produit et on garde les termes dont on est sûr : le reste se lit sur les premiers termes de chaque facteur.',
  },
  dlDivide: {
    label: 'Diviser par une puissance de x',
    hora: 1,
    why: 'Diviser par xᵏ abaisse tous les degrés de k, le reste aussi : o(x²) ÷ x² = o(1).',
  },
  dlQuotient: {
    label: 'Quotient de DL',
    hora: 2,
    why: 'On met en facteur le premier terme du dénominateur, puis 1 ÷ (1 + u) = 1 − u + u² − … avec u → 0.',
  },
  dlLimit: {
    label: 'Lire la limite sur le DL',
    hora: 1,
    why: 'o(1) tend vers 0 : la limite est le terme constant du développement.',
  },
  sameDenominator: {
    label: 'Réduire au même dénominateur',
    hora: 1,
    why: 'Une seule fraction : la forme ∞ − ∞ devient 0 ÷ 0, qu’un développement limité va lever.',
  },
  equivReference: {
    label: 'Équivalent de référence',
    hora: 2,
    why: 'Quand u tend vers 0 : sin u, tan u, arctan u, eᵘ − 1 et ln(1 + u) sont équivalents à u ; 1 − cos u à u² ÷ 2 ; (1 + u)ᵃ − 1 à a × u.',
  },
  equivDL: {
    label: 'Lire l’équivalent sur le DL',
    hora: 1,
    why: 'Le premier terme non nul d’un développement limité est un équivalent de l’expression.',
  },
  equivDominant: {
    label: 'Garder le terme dominant',
    hora: 1,
    why: 'Une somme est équivalente à son terme qui l’emporte : les autres sont négligeables devant lui.',
  },
  equivReplace: {
    label: 'Remplacer par un équivalent',
    hora: 1,
    why: 'Dans un produit ou un quotient, on peut remplacer un facteur par un équivalent (jamais dans une somme).',
  },
  lnRule: {
    label: 'Logarithme d’un produit',
    hora: 1,
    why: 'ln(ab) = ln(a) + ln(b) : on sépare le facteur qui l’emporte.',
  },
  reduce: {
    label: 'Réduire',
    hora: 1,
    why: 'On réduit l’écriture : les termes qui s’annulent disparaissent.',
  },
};

const CARD = 'Limites';

export interface KLimVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  /** ligne que le moteur ne sait pas vérifier, gardée sans HORA */
  unknown?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  lmove?: LMove;
}

/** ce que la ligne apprend au moteur */
interface LMove {
  text: string;
  terms: [string, Term][];
  eqs: [string, string][];
  lims: [string, LimVal][];
  lower: [string, Term][];
  upper: [string, Term][];
  /** les développements établis (v58) */
  dls: [string, Ser][];
  /** v61 : les équivalents établis (classe de A, B) pour « A ∼ B » */
  equivs?: [string, Term][];
  final?: boolean;
}

export interface KLimState {
  model: ScratchModel;
  P: LimPoint;
  target: Term;
  targetKey: string;
  answer: LimVal;
  terms: Map<string, Term>;
  parent: Map<string, string>;
  lim: Map<string, LimVal>;
  lower: Map<string, Term[]>;
  upper: Map<string, Term[]>;
  written: Set<string>;
  givenTexts: Set<string>;
  path: string[];
  traps: { text: string; line: Line; title: string; why: string }[];
  lines: number;
  concluded: boolean;
  /** juge comme le moteur v54 (sans les ajouts du v56) */
  v54: boolean;
  /** v58 : la feuille écrit des développements limités */
  dl: boolean;
  /** le développement établi de chaque classe d'égalité (v58) */
  dls: Map<string, Ser>;
  /** v61 : la feuille cherche un équivalent (« A ∼ B ») : B, sinon null */
  equiv: Term | null;
  /** v61 : les équivalents établis : (classe de A, B) */
  equivs: [string, Term][];
}

/* ------------------------------ lecture ------------------------------ */

export class LimitModelError extends Error {}

const ARROW = '→';
const INF_ATOMS = new Set(['+∞', '−∞']);

/**
 * les bouts d'une ligne : l'expression lue comme partout, puis « → » et la limite ;
 * `raw` : sans les « × » sous-entendus (affichage de l'énoncé, « 3x² + 1 »)
 */
export function klimLineAtoms(text: string, opts?: { raw?: boolean }): string[] {
  // v61 : « A ∼ B », un équivalent (le « ~ » du clavier compris)
  const eqText = text.replace(/~/g, EQUIV);
  const ei = eqText.indexOf(EQUIV);
  if (ei >= 0) {
    const l = eqText.slice(0, ei).trim();
    const r = eqText.slice(ei + 1).trim();
    return [...(l ? klimLineAtoms(l, opts) : []), EQUIV, ...(r ? klimLineAtoms(r, opts) : [])];
  }
  // v58 : un reste « o(x²) » est UN bout
  if (textHasO(text)) {
    const { text: t, outs } = withOPlaceholders(text);
    return restoreOAtoms(klimLineAtoms(t, opts), outs);
  }
  const read = (u: string) => (opts?.raw ? lex(u) : normalize(lex(u)));
  const t = text.replace(/->/g, ARROW);
  const i = t.indexOf(ARROW);
  if (i < 0) return read(t);
  const left = t.slice(0, i).trim();
  const right = t.slice(i + 1).trim();
  return [...(left ? read(left) : []), ARROW, ...valueAtoms(right)];
}

function valueAtoms(right: string): string[] {
  const r = right.replace(/^-/, '−').replace(/\s+/g, '');
  if (r === '+∞' || r === '∞') return ['+∞'];
  if (r === '−∞') return ['−∞'];
  return right ? normalize(lex(right)) : [];
}

/** l'écriture de la ligne (enregistrement, comparaison) */
export function klimText(atoms: string[]): string {
  const ei = atoms.indexOf(EQUIV);
  if (ei >= 0) {
    const l = atoms.slice(0, ei);
    const r = atoms.slice(ei + 1);
    return `${l.length ? klimText(l) + ' ' : ''}${EQUIV}${r.length ? ' ' + klimText(r) : ''}`;
  }
  const i = atoms.indexOf(ARROW);
  if (i < 0) return exprText(atoms);
  const left = atoms.slice(0, i);
  const right = atoms.slice(i + 1);
  return `${left.length ? exprText(left) + ' ' : ''}${ARROW}${right.length ? ' ' + exprText(right) : ''}`;
}
function exprText(atoms: string[]): string {
  return atomsText(atoms)
    .replace(/\//g, '÷')
    .replace(/\s*÷\s*/g, ' ÷ ');
}

const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: klimText(atoms) }] }],
});
/** Ligne de feuille (texte) pour l'app. */
export function klimLine(text: string): Line {
  return lineOf(klimLineAtoms(text));
}

const E: Term = { k: 'fn', f: 'exp', a: { k: 'num', v: { n: 1n, d: 1n } } } as Term;
/** la constante e s'écrit « e » ; « e^(…) » est déjà lu exp(…) */
function withE(t: Term): Term {
  switch (t.k) {
    case 'sym':
      return t.n === 'e' ? E : t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withE) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withE(t.a), b: withE(t.b) } as Term;
    case 'fn':
      return { ...t, a: withE(t.a) };
    default:
      return t;
  }
}

/** une expression seule (null : illisible) */
function exprOf(atoms: string[]): Term | null {
  if (!atoms.length) return null;
  try {
    const p = parseStatement(atomsText(oAtomsToSyms(atoms)));
    return p.ok && p.s.k === 'expr' ? withE(p.s.e) : null;
  } catch {
    return null;
  }
}

export function exprOfText(text: string): Term | null {
  try {
    return exprOf(normalize(lex(text)));
  } catch {
    return null;
  }
}

const keyOf = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};

/** une limite écrite : « +∞ », « −∞ », « 0⁺ », « 3 », « 1 ÷ 2 », « e » */
function valueOf(atoms: string[]): LimVal | null {
  if (atoms.length === 1 && INF_ATOMS.has(atoms[0]!))
    return { k: 'inf', s: atoms[0] === '+∞' ? 1 : -1 };
  if (!atoms.length) return null;
  const last = atoms[atoms.length - 1]!;
  const m = /^(.*\d)([⁺⁻])$/.exec(last);
  let side: 1 | -1 | 0 = 0;
  let body = atoms;
  if (m) {
    side = m[2] === '⁺' ? 1 : -1;
    body = [...atoms.slice(0, -1), m[1]!];
  }
  const t = exprOf(body);
  if (!t || hasAnyVar(t)) return null;
  let v: number;
  try {
    const r = knownLimit(t, { x: '·', inf: 1, a: 0, side: 0, seq: false });
    if (r.k !== 'fin') return null;
    v = r.v;
  } catch {
    return null;
  }
  if (side && Math.abs(v) > 1e-12) return null;
  return fin(v, side);
}
function hasAnyVar(t: Term): boolean {
  switch (t.k) {
    case 'sym':
      return t.n !== 'π';
    case 'num':
      return false;
    case 'add':
    case 'mul':
      return t.c.some(hasAnyVar);
    case 'fn':
      return hasAnyVar(t.a);
    default:
      return hasAnyVar(t.a) || hasAnyVar(t.b);
  }
}

/** le point, la variable et l'expression de la question */
export function limitQuestionOf(model: ScratchModel): {
  P: LimPoint;
  target: Term;
  answer: LimVal;
  /** v61 : l'équivalent cherché (réponse « A ∼ B ») */
  equiv: Term | null;
} {
  const spec = model.kernel!;
  const x = spec.goal.target;
  const point = spec.point;
  if (!point) throw new LimitModelError('Le point (`point`) manque.');
  const P = parsePoint(point, x, x === 'n');
  const target = exprOfText(spec.start);
  if (!target) throw new LimitModelError(`Expression illisible : « ${spec.start} ».`);
  if (!hasVar(target, x)) throw new LimitModelError(`L’expression ne dépend pas de ${x}.`);
  const atoms = klimLineAtoms(spec.answer);
  // v61 : un équivalent
  const ei = atoms.indexOf(EQUIV);
  if (ei >= 0) {
    const left = exprOf(atoms.slice(0, ei));
    const eq = exprOf(atoms.slice(ei + 1));
    if (!left || keyOf(left) !== keyOf(target))
      throw new LimitModelError('La réponse commence par l’expression de la question.');
    if (!eq || !hasVar(eq, x))
      throw new LimitModelError('L’équivalent de la réponse est illisible.');
    return { P, target, answer: fin(1), equiv: eq };
  }
  const i = atoms.indexOf(ARROW);
  if (i < 0) throw new LimitModelError('La réponse s’écrit « expression → limite ».');
  const left = exprOf(atoms.slice(0, i));
  const answer = valueOf(atoms.slice(i + 1));
  if (!left || keyOf(left) !== keyOf(target))
    throw new LimitModelError('La réponse commence par l’expression de la question.');
  if (!answer) throw new LimitModelError('La limite de la réponse est illisible.');
  return { P, target, answer, equiv: null };
}

export function createKLimState(model: ScratchModel, opts?: { v54?: boolean }): KLimState {
  const spec = model.kernel!;
  const q = limitQuestionOf(model);
  if (opts?.v54) q.P = { ...q.P, v54: true };
  if (usesDL(model)) q.P = { ...q.P, dl: true };
  // v61 : un équivalent en 0 se lit aussi sur les développements limités
  if (q.equiv && !q.P.inf && Math.abs(q.P.a) < 1e-12) q.P = { ...q.P, dl: true };
  const traps = (model.traps ?? []).map((t) => ({
    text: klimText(klimLineAtoms(t.line)),
    line: klimLine(t.line),
    title: t.title ?? 'Attention',
    why: t.why,
  }));
  const targetKey = keyOf(q.target);
  const givenTexts = new Set([
    klimText(klimLineAtoms(spec.start)),
    ...limitGivenTexts(model).map((t) => klimText(klimLineAtoms(t))),
  ]);
  return {
    model,
    P: q.P,
    target: q.target,
    targetKey,
    answer: q.answer,
    terms: new Map([[targetKey, q.target]]),
    parent: new Map(),
    lim: new Map(),
    lower: new Map(),
    upper: new Map(),
    written: new Set(),
    givenTexts,
    path: spec.path ?? [],
    traps,
    lines: 0,
    concluded: false,
    v54: !!opts?.v54,
    dl: usesDL(model) || !!q.P.dl,
    dls: new Map(),
    equiv: q.equiv,
    equivs: [],
  };
}

/** v58 : la feuille écrit un reste « o(…) » (corrigé, réponse ou énoncé) */
export function usesDL(model: ScratchModel): boolean {
  const spec = model.kernel;
  if (!spec) return false;
  return [spec.start, spec.answer, ...(spec.path ?? []), ...(model.givens ?? [])].some(textHasO);
}

/** les lignes de l'énoncé : « x → +∞ », puis l'expression */
export function limitGivenTexts(model: ScratchModel): string[] {
  const spec = model.kernel!;
  return [
    `${spec.goal.target} ${ARROW} ${spec.point ?? ''}`.trim(),
    spec.start,
    ...(model.givens ?? []),
  ];
}

/* ------------------------------ classes d'égalité ------------------------------ */

function find(ks: { parent: Map<string, string> }, k: string): string {
  let r = k;
  while (ks.parent.has(r) && ks.parent.get(r) !== r) r = ks.parent.get(r)!;
  return r;
}

/** une vue de l'état avec les égalités et limites de la ligne en cours */
interface View {
  parent: Map<string, string>;
  lim: Map<string, LimVal>;
  lower: Map<string, Term[]>;
  upper: Map<string, Term[]>;
  dls: Map<string, Ser>;
}
function viewOf(ks: KLimState): View {
  return {
    parent: new Map(ks.parent),
    lim: new Map(ks.lim),
    lower: new Map([...ks.lower].map(([k, v]) => [k, [...v]])),
    upper: new Map([...ks.upper].map(([k, v]) => [k, [...v]])),
    dls: new Map(ks.dls),
  };
}
/** garder le développement le plus précis d'une classe */
function setDL(v: { parent: Map<string, string>; dls: Map<string, Ser> }, k: string, s: Ser): void {
  const r = find(v, k);
  const old = v.dls.get(r);
  if (!old || s.e > old.e) v.dls.set(r, s);
}
const dlOf = (v: View, k: string): Ser | null => v.dls.get(find(v, k)) ?? null;
function union(v: View, a: string, b: string): void {
  const ra = find(v, a);
  const rb = find(v, b);
  if (ra === rb) return;
  const la = v.lim.get(ra);
  const lb = v.lim.get(rb);
  v.parent.set(rb, ra);
  if (!la && lb) v.lim.set(ra, lb);
  const da = v.dls.get(ra);
  const db = v.dls.get(rb);
  if (db && (!da || db.e > da.e)) v.dls.set(ra, db);
  const lo = [...(v.lower.get(ra) ?? []), ...(v.lower.get(rb) ?? [])];
  const up = [...(v.upper.get(ra) ?? []), ...(v.upper.get(rb) ?? [])];
  if (lo.length) v.lower.set(ra, lo);
  if (up.length) v.upper.set(ra, up);
}
const limOf = (v: View, k: string): LimVal | null => v.lim.get(find(v, k)) ?? null;
const lookupIn =
  (v: View) =>
  (t: Term): LimVal | null =>
    limOf(v, keyOf(t));

/* ------------------------------ morceaux immédiats ------------------------------ */

/**
 * Une constante, une référence, ou UNE opération entre une constante et une référence
 * (« 3 ÷ x », « x − 2 », « −x² », « 2eˣ ») : pas besoin d'une ligne.
 */
function freeLimit(u: Term, P: LimPoint): LimVal | null {
  if (!hasVar(u, P.x)) {
    const r = knownLimit(u, P);
    return r.k === 'fin' ? r : null;
  }
  if (opsCount(u, P) > FREE_OPS) return null;
  const r = knownLimit(u, P, { noTheorems: true });
  return isDet(r) ? r : null;
}

/** au plus deux opérations « visibles » : « √(x + 1) », « 1 − 5 ÷ x² », « 3 ÷ (x − 2) » */
const FREE_OPS = 2;

/**
 * Les opérations à faire de tête : une constante, x et les références n'en comptent
 * aucune ; multiplier par une constante (ou changer le signe) non plus ; chaque somme avec
 * une constante, quotient d'une constante, puissance ou fonction d'autre chose que x en
 * compte une. Deux morceaux variables : jamais immédiat.
 */
function opsCount(u: Term, P: LimPoint): number {
  const x = P.x;
  if (!hasVar(u, x)) return 0;
  if (u.k === 'sym' || referenceLimit(u, P)) return 0;
  const vary = (cs: Term[]) => cs.filter((c) => hasVar(c, x));
  switch (u.k) {
    case 'mul': {
      const v = vary(u.c);
      return v.length === 1 ? opsCount(v[0]!, P) : 99;
    }
    case 'add': {
      const v = vary(u.c);
      return v.length === 1 ? 1 + opsCount(v[0]!, P) : 99;
    }
    case 'div':
      if (!hasVar(u.b, x)) return opsCount(u.a, P);
      if (!hasVar(u.a, x)) return 1 + opsCount(u.b, P);
      return 99;
    case 'pow':
      if (!hasVar(u.b, x)) return 1 + opsCount(u.a, P);
      return 99;
    case 'fn':
      return 1 + opsCount(u.a, P);
    default:
      return 99;
  }
}

function availLimit(u: Term, P: LimPoint, v: View): LimVal | null {
  return limOf(v, keyOf(u)) ?? freeLimit(u, P) ?? scaledLimit(u, P, v);
}

/**
 * Un morceau déjà établi, multiplié ou divisé par une constante (« −ln(x) ÷ x » quand
 * « ln(x) ÷ x → 0 » est écrit) : changer de signe n'est pas une opération de plus.
 */
function scaledLimit(u: Term, P: LimPoint, v: View): LimVal | null {
  const x = P.x;
  const one: Term = { k: 'num', v: { n: 1n, d: 1n } } as Term;
  let consts: Term[] = [];
  let core: Term | null = null;
  if (u.k === 'mul') {
    const vs = u.c.filter((c) => hasVar(c, x));
    if (vs.length === 1 && vs.length < u.c.length) {
      consts = u.c.filter((c) => !hasVar(c, x));
      core = vs[0]!;
    }
  } else if (u.k === 'div' && !hasVar(u.b, x) && hasVar(u.a, x)) {
    consts = [{ k: 'div', a: one, b: u.b } as Term];
    core = u.a;
  } else if (u.k === 'div' && u.a.k === 'mul') {
    const vs = u.a.c.filter((c) => hasVar(c, x));
    if (vs.length === 1 && vs.length < u.a.c.length) {
      consts = u.a.c.filter((c) => !hasVar(c, x));
      core = { k: 'div', a: vs[0]!, b: u.b } as Term;
    }
  }
  if (!core) return null;
  const inner = limOf(v, keyOf(core));
  if (!inner) return null;
  const r = mulL([
    ...consts.map((c) => ({ r: fin(constValue(c)) as LimRes, c: true })),
    { r: inner, c: false },
  ]);
  return isDet(r) ? r : null;
}

/** la limite par UNE opération sur des morceaux connus (établis ou immédiats) */
function opStep(M: Term, P: LimPoint, v: View): { r: LimRes; kind: StepKind } | null {
  const arg = (u: Term) => {
    const r = availLimit(u, P, v);
    return r ? { r: r as LimRes, c: !hasVar(u, P.x) } : null;
  };
  switch (M.k) {
    case 'add': {
      const a = M.c.map(arg);
      if (a.some((x) => !x)) return null;
      return { r: addL(a as never), kind: 'sum' };
    }
    case 'mul': {
      const a = M.c.map(arg);
      if (a.some((x) => !x)) return null;
      return { r: mulL(a as never), kind: 'product' };
    }
    case 'div': {
      const a = arg(M.a);
      const b = arg(M.b);
      if (!a || !b) return null;
      return { r: divL(a, b), kind: 'quotient' };
    }
    case 'pow': {
      const a = arg(M.a);
      const b = arg(M.b);
      if (!a || !b) return null;
      return { r: powL(a, b, { seq: P.seq }), kind: hasVar(M.b, P.x) ? 'composition' : 'power' };
    }
    case 'fn': {
      const a = arg(M.a);
      if (!a) return null;
      return { r: fnL(M.f, a, { v54: P.v54 }), kind: 'composition' };
    }
    default:
      return null;
  }
}

/* ------------------------------ verdicts ------------------------------ */

type Base = { atoms: string[]; line: Line };

function stepVerdict(base: Base, steps: StepKind[], lmove: LMove, final: boolean): KLimVerdict {
  const hora = steps.reduce((s, k) => s + STEP[k].hora, 0);
  const main = steps.reduce((m, k) => (STEP[k].hora > STEP[m].hora ? k : m), steps[0]!);
  const labels = [...new Set(steps.map((k) => STEP[k].label))];
  const why =
    [...new Set(steps)].map((k) => STEP[k].why).join(' ') + (final ? ' C’est la réponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: { id: LIMIT_STEP_IDS[main], card: CARD, label: labels.join(' → '), hora, why },
    lmove: { ...lmove, ...(final ? { final } : {}) },
  };
}

function jumpVerdict(base: Base, lmove: LMove, final: boolean, message?: string): KLimVerdict {
  return {
    kind: 'useless',
    ...base,
    jump: true,
    final,
    title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
    message:
      message ??
      (final
        ? 'C’est la bonne limite. Détaille ton raisonnement, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une limite à la fois, pour gagner des HORA. Tu peux la garder sans HORA.'),
    lmove: { ...lmove, ...(final ? { final } : {}) },
  };
}

function trapOr(ks: KLimState, base: Base, why: { title: string; message: string }): KLimVerdict {
  const text = klimText(base.atoms);
  const t = ks.traps.find((t) => t.text === text);
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, ...why };
}

const emptyMove = (text: string): LMove => ({
  text,
  terms: [],
  eqs: [],
  lims: [],
  lower: [],
  upper: [],
  dls: [],
});

/* ------------------------------ juge ------------------------------ */

function knownPrefix(ks: KLimState, A: string[]): boolean {
  return ks.path.some((t) => {
    let atoms: string[];
    try {
      atoms = klimLineAtoms(t);
    } catch {
      return false;
    }
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

const CMP = new Set(['<', '>', '≤', '≥']);

export function klimJudge(ks: KLimState, chips: Chip[], opts?: { verify?: boolean }): KLimVerdict {
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = knownPrefix(ks, A);
  const incomplete = (): KLimVerdict =>
    opts?.verify
      ? { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' }
      : { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  // v61 : « A ∼ B », un équivalent
  if (A.includes(EQUIV)) {
    const v = judgeEquivLine(ks, chips, norm.src, A, prefix, incomplete, opts);
    if (v.kind === 'syntax' || v.kind === 'building' || v.kind === 'open' || v.kind === 'empty')
      return v;
    if (prefix && !opts?.verify) {
      if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
      return { kind: 'building', atoms: A, complete: true };
    }
    if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
      return { kind: 'pending', atoms: A, complete: true };
    return { ...v, complete: true };
  }
  const arrows = A.filter((a) => a === ARROW).length;
  if (arrows > 1)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Une seule flèche',
      message: 'La flèche « → » se met une fois, à la fin : « expression → limite ».',
      badChip: chips.length - 1,
    };
  const ai = A.indexOf(ARROW);
  const left = ai < 0 ? A : A.slice(0, ai);
  const right = ai < 0 ? [] : A.slice(ai + 1);
  if (ai === 0)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: 'Écris d’abord l’expression, puis « → » et sa limite.',
      badChip: 0,
    };
  if (right.some((a) => a === '=' || a === ARROW || CMP.has(a)))
    return {
      kind: 'syntax',
      atoms: A,
      title: 'La limite à la fin',
      message: 'Après « → », écris seulement la limite : « +∞ », « 0⁺ », « 3 »…',
      badChip: chips.length - 1,
    };
  if (left.some((a) => INF_ATOMS.has(a)))
    return {
      kind: 'syntax',
      atoms: A,
      title: '∞ n’est pas un nombre',
      message: '« +∞ » s’écrit seulement après « → », comme limite.',
      badChip: norm.src[left.findIndex((a) => INF_ATOMS.has(a))] ?? chips.length - 1,
    };
  const syn = checkSyntax(left, { chains: true });
  if (!syn.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: norm.src[syn.index] !== undefined ? norm.src[syn.index]! : chips.length - 1,
    };
  }
  if (!syn.complete) return incomplete();
  if (ai >= 0 && !right.length) return incomplete();
  const hasCmp = left.some((a) => CMP.has(a));
  const hasEq = left.includes('=');
  if (ai < 0 && !hasEq && !hasCmp) {
    if (opts?.verify)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une relation',
        message: 'Écris une limite (« … → +∞ »), une égalité (« … = … ») ou un encadrement.',
      };
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const base: Base = { atoms: A, line: lineOf(A) };
  let v: KLimVerdict;
  if (hasCmp) {
    if (hasEq || ai >= 0)
      return {
        kind: 'syntax',
        atoms: A,
        title: 'Une chose par ligne',
        message: 'Un encadrement s’écrit seul sur sa ligne, sans « = » ni « → ».',
      };
    v = judgeBound(ks, base, left);
  } else {
    const members = splitTop(left, '=').map(exprOf);
    if (members.some((m) => !m))
      return {
        kind: 'syntax',
        atoms: A,
        title: 'Ça ne se lit pas',
        message: 'Une expression ne se lit pas : vérifie les parenthèses.',
        badChip: chips.length - 1,
      };
    let value: LimVal | null = null;
    if (ai >= 0) {
      value = valueOf(right);
      if (!value) {
        const rs = checkSyntax(right);
        if (rs.ok && !rs.complete) return incomplete();
      }
      if (!value)
        return {
          kind: 'syntax',
          atoms: A,
          title: 'Une limite',
          message: 'Une limite s’écrit « +∞ », « −∞ », un nombre, ou « 0⁺ », « 0⁻ ».',
          badChip: chips.length - 1,
        };
    }
    const Ms = members as Term[];
    if (Ms.some(hasO)) {
      if (!ks.dl)
        return {
          kind: 'syntax',
          atoms: A,
          title: 'Ça ne se lit pas',
          message: 'Une expression ne se lit pas : vérifie les parenthèses.',
          badChip: chips.length - 1,
        };
      v = judgeDLChain(ks, base, Ms, value);
    } else v = judgeChain(ks, base, Ms, value);
  }
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

function splitTop(atoms: string[], sep: string): string[][] {
  const out: string[][] = [[]];
  let depth = 0;
  for (const a of atoms) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && a === sep) out.push([]);
    else out[out.length - 1]!.push(a);
  }
  return out;
}

/* ------------------------------ égalités ------------------------------ */

type Part =
  | { k: 'step'; steps: StepKind[] }
  | { k: 'none' }
  | { k: 'jump'; message?: string }
  | { k: 'unknown' }
  | { k: 'false'; title: string; message: string };

function rootHas(t: Term): boolean {
  switch (t.k) {
    case 'fn':
      return t.f === 'sqrt' || rootHas(t.a);
    case 'add':
    case 'mul':
      return t.c.some(rootHas);
    case 'div':
    case 'pow':
      return rootHas(t.a) || rootHas(t.b);
    default:
      return false;
  }
}

/** une somme qui contient une racine (l'expression conjuguée), ou un produit qui en a une */
function rootSum(u: Term): boolean {
  if (u.k === 'add') return u.c.some(rootHas);
  if (u.k === 'mul') return u.c.some(rootSum);
  return false;
}

/**
 * Multiplier par l'expression conjuguée, déjà simplifié : une différence avec une racine
 * devenue un quotient dont le dénominateur est la somme conjuguée.
 */
function conjugateShape(A: Term, B: Term): boolean {
  const diff = (u: Term): boolean =>
    (u.k === 'add' && u.c.some(rootHas)) || (u.k === 'div' && diff(u.a));
  return rootHas(A) && diff(A) && B.k === 'div' && rootSum(B.b) && !rootSum(B.a);
}

/** un morceau « c ÷ xᵏ » dans une somme : on a divisé par le terme dominant */
function hasInverseTerm(t: Term, x: string): boolean {
  const inv = (u: Term): boolean =>
    (u.k === 'div' && hasVar(u.b, x) && u.b.k !== 'add') ||
    // qⁿ avec |q| < 1 : on a divisé par la plus grande puissance
    (u.k === 'pow' && !hasVar(u.a, x) && hasVar(u.b, x) && Math.abs(constValue(u.a)) < 1) ||
    (u.k === 'mul' && u.c.some(inv));
  switch (t.k) {
    case 'add':
      return t.c.some((c) => inv(c) || hasInverseTerm(c, x));
    case 'mul':
      return t.c.some((c) => hasInverseTerm(c, x));
    case 'div':
    case 'pow':
      return hasInverseTerm(t.a, x) || hasInverseTerm(t.b, x);
    case 'fn':
      return hasInverseTerm(t.a, x);
    default:
      return false;
  }
}

/** le geste d'une écriture qui lève l'indétermination */
function rewriteKind(A: Term, B: Term, P: LimPoint): StepKind {
  // une différence de racines devenue un quotient
  if (conjugateShape(A, B)) return 'conjugate';
  // une fraction rationnelle qu'on simplifie (en un réel)
  if (!P.inf) return 'simplify';
  // diviser en haut et en bas par le terme dominant
  if (A.k === 'div' && B.k === 'div') return hasInverseTerm(B, P.x) ? 'factorDominant' : 'simplify';
  // un facteur dominant mis en évidence : x³(…), eˣ(…), (x(…)) ÷ (x(…))
  const isMonoFactor = (u: Term) => {
    if (!hasVar(u, P.x)) return false;
    const r = toRatFnSafe(u);
    return u.k === 'pow' || u.k === 'sym' || (u.k === 'fn' && u.f === 'exp') || !!r;
  };
  const factored = (u: Term): boolean =>
    u.k === 'mul' && u.c.some(isMonoFactor) && u.c.some((c) => c.k === 'add');
  if (factored(B) || (B.k === 'div' && (factored(B.a) || factored(B.b)))) return 'factorDominant';
  if (B.k === 'div' && A.k === 'div') return 'simplify';
  return 'rewrite';
}

/** un facteur commun (variable) en haut et en bas d'un quotient, un produit d'un côté */
function exposesCommonFactor(B: Term, x: string): boolean {
  if (B.k !== 'div') return false;
  const fs = (u: Term) => (u.k === 'mul' ? u.c : [u]).filter((c) => hasVar(c, x)).map(keyOf);
  const top = fs(B.a);
  const bot = fs(B.b);
  if (top.length + bot.length < 3) return false;
  return bot.some((k) => top.includes(k));
}

function judgeRewrite(ks: KLimState, v: View, A: Term, B: Term): Part {
  const kA = keyOf(A);
  const kB = keyOf(B);
  if (kA === kB || find(v, kA) === find(v, kB)) return { k: 'none' };
  if (!sameNear(A, B, ks.P))
    return {
      k: 'false',
      title: 'Pas la même expression',
      message: 'Ces deux écritures ne sont pas égales près du point : vérifie ton calcul.',
    };
  // v61 : une feuille d'équivalents — une réécriture utile prépare l'équivalent
  if (ks.equiv) {
    if (conjugateShape(A, B)) return { k: 'step', steps: ['conjugate'] };
    if (sameDenominatorShape(A, B, ks.P.x)) return { k: 'step', steps: ['sameDenominator'] };
    if (expFormShape(A, B, ks.P.x)) return { k: 'step', steps: ['expForm'] };
    // uᵛ écrit e^(v × ln(u)) dans un seul terme d'une somme (« x^x − 1 »)
    if (A.k === 'add' && B.k === 'add' && A.c.length === B.c.length) {
      const d = A.c.map((a, i) => [a, B.c[i]!] as const).filter(([a, b]) => keyOf(a) !== keyOf(b));
      if (d.length === 1 && expFormShape(d[0]![0], d[0]![1], ks.P.x))
        return { k: 'step', steps: ['expForm'] };
    }
    if (lnCount(B) > lnCount(A) && lnCount(A) > 0) return { k: 'step', steps: ['lnRule'] };
    // des termes qui s'annulent : une écriture plus courte
    if (termSize(B) < termSize(A) && termsOfAdd(B).length < termsOfAdd(A).length)
      return { k: 'step', steps: ['reduce'] };
    if (exposesCommonFactor(B, ks.P.x)) return { k: 'step', steps: ['factor'] };
    return { k: 'none' };
  }
  const lookup = lookupIn(v);
  // v54 : rien à gagner dès qu'une des deux limites est établie ; v56 : réécrire
  // l'expression en une écriture dont la limite est établie la lui transmet
  if (ks.v54 ? limOf(v, kA) || limOf(v, kB) : limOf(v, kA)) return { k: 'none' };
  // l'écriture de départ, par les seules opérations (même si un théorème la donne) ;
  // l'écriture d'arrivée, par tout le cours (bornée comprise)
  const la = knownLimit(A, ks.P, { lookup, noTheorems: true });
  const lb = knownLimit(B, ks.P, { lookup, bnd: true });
  if (isDet(la)) return { k: 'none' };
  if (isDet(lb)) return { k: 'step', steps: [rewriteKind(A, B, ks.P)] };
  if (exposesCommonFactor(B, ks.P.x))
    return {
      k: 'step',
      steps: [rootSum(B.k === 'div' ? B.b : B) && rootHas(A) ? 'conjugateMul' : 'factor'],
    };
  if (conjugateShape(A, B)) return { k: 'step', steps: ['conjugate'] };
  // v56 : uᵛ (exposant variable) écrit e^(v × ln(u))
  if (!ks.v54 && expFormShape(A, B, ks.P.x)) return { k: 'step', steps: ['expForm'] };
  // v58 : plusieurs fractions réunies en une seule
  if (ks.dl && sameDenominatorShape(A, B, ks.P.x)) return { k: 'step', steps: ['sameDenominator'] };
  return { k: 'none' };
}

/** une somme d'au moins deux fractions (dénominateurs variables) devenue une fraction */
function sameDenominatorShape(A: Term, B: Term, x: string): boolean {
  if (A.k !== 'add' || B.k !== 'div') return false;
  const frac = (u: Term): boolean =>
    (u.k === 'div' && hasVar(u.b, x)) ||
    (u.k === 'mul' && u.c.some(frac)) ||
    (u.k === 'pow' && constValue(u.b) < 0);
  return A.c.filter(frac).length >= 2;
}

/** A = uᵛ, l'exposant dépend de la variable ; B = e^(…) avec un ln de u */
function expFormShape(A: Term, B: Term, x: string): boolean {
  if (A.k !== 'pow' || !hasVar(A.b, x) || !hasVar(A.a, x)) return false;
  if (B.k !== 'fn' || B.f !== 'exp') return false;
  const base = keyOf(A.a);
  const hasLnOf = (u: Term): boolean => {
    switch (u.k) {
      case 'fn':
        return (u.f === 'ln' && keyOf(u.a) === base) || hasLnOf(u.a);
      case 'add':
      case 'mul':
        return u.c.some(hasLnOf);
      case 'div':
      case 'pow':
        return hasLnOf(u.a) || hasLnOf(u.b);
      default:
        return false;
    }
  };
  return hasLnOf(B.a);
}

/* ------------------------------ limites ------------------------------ */

function fiForm(M: Term, P: LimPoint, v: View): string | null {
  const r = knownLimit(M, P, { lookup: lookupIn(v), noTheorems: true });
  return r.k === 'fi' ? r.form : null;
}

function judgeLimit(ks: KLimState, v: View, M: Term, val: LimVal): Part {
  const lookup = lookupIn(v);
  const truth = trueLimit(M, ks.P, { lookup });
  if (!truth.v) return { k: 'unknown' };
  const t = truth.v;
  if (!sameLimit(val, t)) {
    if (val.k === 'inf' && t.k === 'inf')
      return {
        k: 'false',
        title: 'Le signe',
        message: 'Vers +∞ ou vers −∞ ? Regarde le signe de chaque morceau.',
      };
    if (val.k === 'fin' && t.k === 'fin' && Math.abs(val.v) < 1e-12 && Math.abs(t.v) < 1e-12)
      return {
        k: 'false',
        title: 'Le côté',
        message:
          val.side && !t.side
            ? 'On ne sait pas de quel côté elle tend vers 0 : écris seulement « 0 ».'
            : '0⁺ ou 0⁻ : regarde le signe de l’expression près du point.',
      };
    const form = fiForm(M, ks.P, v);
    if (form)
      return {
        k: 'false',
        title: 'Forme indéterminée',
        message: `« ${form} » est une forme indéterminée : on ne conclut pas par les opérations. Transforme d’abord l’écriture.`,
      };
    if (t.k === 'none' || t.k === 'bnd')
      return {
        k: 'false',
        title: 'Ce n’est pas la limite',
        message: 'Regarde comment l’expression se comporte près du point.',
      };
    return {
      k: 'false',
      title: 'Ce n’est pas la limite',
      message: 'Reprends la limite de chaque morceau.',
    };
  }
  const known = limOf(v, keyOf(M));
  if (known && sameLimit(val, known)) return { k: 'none' };
  // v58 : la limite se lit sur un développement établi
  if (ks.dl) {
    const s = dlOf(v, keyOf(M));
    const L = s ? serLimit(s, ks.P.side) : null;
    if (L && limMatches(val, L)) return { k: 'step', steps: ['dlLimit'] };
  }
  // une limite de référence
  if (referenceLimit(M, ks.P)) return { k: 'step', steps: ['reference'] };
  // une opération sur des morceaux connus
  const op = opStep(M, ks.P, v);
  if (op && isDet(op.r) && sameLimit(val, op.r)) return { k: 'step', steps: [op.kind] };
  if (op && isDet(op.r) && val.k === 'fin' && op.r.k === 'fin' && sameLimit(fin(val.v), op.r))
    return { k: 'step', steps: [op.kind] };
  // par continuité, en un réel
  if (!ks.P.inf && continuousAt(M, ks.P) !== null && (val.k !== 'fin' || !val.side))
    return { k: 'step', steps: ['continuity'] };
  // les théorèmes du cours (v56 : aussi sur une composée)
  const th = theoremLimit(M, ks.P);
  const thKind: StepKind | null = !th
    ? null
    : th.composite
      ? th.via === 'derivative'
        ? 'derivativeComposite'
        : 'compareComposite'
      : th.via;
  if (th && thKind && sameLimit(val, th.v)) return { k: 'step', steps: [thKind] };
  if (th && thKind && val.k === 'fin' && th.v.k === 'fin' && sameLimit(fin(val.v), th.v))
    return { k: 'step', steps: [thKind] };
  // gendarmes, comparaison
  const root = find(v, keyOf(M));
  const quick = (u: Term): LimVal | null => {
    const r = knownLimit(u, ks.P, { lookup });
    return isDet(r) ? r : null;
  };
  const lo = (v.lower.get(root) ?? []).map(quick);
  const up = (v.upper.get(root) ?? []).map(quick);
  if (
    val.k === 'fin' &&
    lo.some((l) => l?.k === 'fin' && Math.abs(l.v - val.v) < 1e-9) &&
    up.some((u) => u?.k === 'fin' && Math.abs(u.v - val.v) < 1e-9)
  )
    return { k: 'step', steps: ['squeeze'] };
  if (val.k === 'inf' && val.s > 0 && lo.some((l) => l?.k === 'inf' && l.s > 0))
    return { k: 'step', steps: ['comparison'] };
  if (val.k === 'inf' && val.s < 0 && up.some((u) => u?.k === 'inf' && u.s < 0))
    return { k: 'step', steps: ['comparison'] };
  const form = fiForm(M, ks.P, v);
  if (form)
    return {
      k: 'jump',
      message: `C’est juste, mais « ${form} » est une forme indéterminée : montre comment la lever (factoriser, simplifier…). Tu peux garder cette ligne sans HORA.`,
    };
  return { k: 'jump' };
}

/* ------------------------------ une ligne « = … → » ------------------------------ */

function judgeChain(ks: KLimState, base: Base, members: Term[], value: LimVal | null): KLimVerdict {
  const text = klimText(base.atoms);
  if (ks.givenTexts.has(text))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà dans l’énoncé.',
    };
  const view = viewOf(ks);
  const move = emptyMove(text);
  const steps: StepKind[] = [];
  let jump: string | undefined | null = null;
  let unknown = false;
  members.forEach((m) => move.terms.push([keyOf(m), m]));
  for (let i = 1; i < members.length; i++) {
    const r = judgeRewrite(ks, view, members[i - 1]!, members[i]!);
    if (r.k === 'false') return trapOr(ks, base, { title: r.title, message: r.message });
    if (r.k === 'step') steps.push(...r.steps);
    union(view, keyOf(members[i - 1]!), keyOf(members[i]!));
    move.eqs.push([keyOf(members[i - 1]!), keyOf(members[i]!)]);
  }
  if (value) {
    const M = members[members.length - 1]!;
    const r = judgeLimit(ks, view, M, value);
    if (r.k === 'false') return trapOr(ks, base, { title: r.title, message: r.message });
    if (r.k === 'step') steps.push(...r.steps);
    if (r.k === 'jump') jump = r.message;
    if (r.k === 'unknown') unknown = true;
    if (r.k !== 'unknown') {
      // une limite juste : on garde la plus précise (son côté)
      const truth = trueLimit(M, ks.P, { lookup: lookupIn(view) }).v;
      const keep = truth && truth.k === 'fin' && truth.side ? truth : value;
      move.lims.push([keyOf(M), keep]);
      view.lim.set(find(view, keyOf(M)), keep);
    }
  }
  const final = ks.equiv
    ? !ks.concluded && equivReachesAnswer(ks, view, ks.equivs)
    : !!view.lim.get(find(view, ks.targetKey)) && !ks.lim.get(find(ks, ks.targetKey));
  if (unknown)
    return {
      kind: 'useless',
      ...base,
      unknown: true,
      title: 'Je ne sais pas encore vérifier ce passage',
      message: 'Ta ligne reste sur la feuille, sans HORA.',
      lmove: move,
    };
  if (jump !== null) return jumpVerdict(base, move, final, jump);
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: members.length > 1 && !value ? 'Juste, mais ça ne t’avance pas' : 'Rien de nouveau',
      message:
        members.length > 1 && !value
          ? 'Les deux écritures sont égales, mais la limite ne se lit pas mieux. Cherche une écriture qui n’est plus une forme indéterminée.'
          : 'Cette limite est déjà écrite.',
      lmove: move,
      final,
    };
  return stepVerdict(base, steps, move, final);
}

/* ------------------------------ équivalents (v61) ------------------------------ */

/** la taille d'une écriture (nombre de nœuds) */
function termSize(t: Term): number {
  switch (t.k) {
    case 'add':
    case 'mul':
      return 1 + t.c.reduce((n, c) => n + termSize(c), 0);
    case 'fn':
      return 1 + termSize(t.a);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + termSize(t.a) + termSize(t.b);
    default:
      return 1;
  }
}
/** les termes d'une somme (sommes imbriquées aplaties) */
function termsOfAdd(t: Term): Term[] {
  return t.k === 'add' ? t.c.flatMap(termsOfAdd) : [t];
}

/** le nombre de logarithmes d'une écriture */
function lnCount(t: Term): number {
  switch (t.k) {
    case 'fn':
      return (t.f === 'ln' ? 1 : 0) + lnCount(t.a);
    case 'add':
    case 'mul':
      return t.c.reduce((n, c) => n + lnCount(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return lnCount(t.a) + lnCount(t.b);
    default:
      return 0;
  }
}

/**
 * Ce qui est équivalent à la classe de `key` : les équivalents écrits (dans les deux sens)
 * et les classes d'égalité qu'ils relient.
 */
function equivClasses(
  v: { parent: Map<string, string> },
  list: [string, Term][],
  key: string,
): { roots: Set<string>; terms: Term[] } {
  const roots = new Set([find(v, key)]);
  const terms: Term[] = [];
  const used = new Set<number>();
  let changed = true;
  while (changed) {
    changed = false;
    list.forEach(([k, t], i) => {
      if (used.has(i)) return;
      const rk = find(v, k);
      const rt = find(v, keyOf(t));
      if (roots.has(rk) || roots.has(rt)) {
        used.add(i);
        terms.push(t);
        roots.add(rk);
        roots.add(rt);
        changed = true;
      }
    });
  }
  return { roots, terms };
}

/** l'expression de la question est équivalente à l'équivalent cherché */
function equivReachesAnswer(
  ks: KLimState,
  v: { parent: Map<string, string> },
  list: [string, Term][],
): boolean {
  if (!ks.equiv) return false;
  const { roots, terms } = equivClasses(v, list, ks.targetKey);
  if (roots.has(find(v, keyOf(ks.equiv)))) return true;
  return terms.some((t) => sameNear(t, ks.equiv!, ks.P));
}

const EQUIV_JUMP = 'un développement limité, un équivalent de référence ou le terme dominant.';

function judgeEquivLine(
  ks: KLimState,
  chips: Chip[],
  src: number[],
  A: string[],
  prefix: boolean,
  incomplete: () => KLimVerdict,
  opts?: { verify?: boolean },
): KLimVerdict {
  const syntax = (title: string, message: string, bad?: number): KLimVerdict => ({
    kind: 'syntax',
    atoms: A,
    title,
    message,
    badChip: bad !== undefined && src[bad] !== undefined ? src[bad]! : chips.length - 1,
  });
  if (!ks.equiv)
    return syntax(
      'Pas d’équivalent ici',
      'Cette feuille cherche une limite : écris « expression → limite ».',
    );
  if (A.filter((a) => a === EQUIV).length > 1)
    return syntax('Un seul « ∼ »', 'Un équivalent par ligne : « expression ∼ équivalent ».');
  const ei = A.indexOf(EQUIV);
  if (ei === 0)
    return syntax(
      'Ça commence mal',
      'Écris d’abord l’expression, puis « ∼ » et son équivalent.',
      0,
    );
  if (A.includes(ARROW) || A.some((a) => CMP.has(a)))
    return syntax(
      'Une chose par ligne',
      'Un équivalent s’écrit seul sur sa ligne, sans « → » ni inégalité.',
    );
  const left = A.slice(0, ei);
  const right = A.slice(ei + 1);
  const inf = A.findIndex((a) => INF_ATOMS.has(a));
  if (inf >= 0)
    return syntax('∞ n’est pas un nombre', '« +∞ » s’écrit seulement comme une limite.', inf);
  const synL = checkSyntax(left, { chains: true });
  if (!synL.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return syntax('Ça commence mal', synL.msg, synL.index);
  }
  if (!synL.complete) return incomplete();
  if (!right.length) return incomplete();
  const synR = checkSyntax(right, { chains: true });
  if (!synR.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return syntax('Ça ne se lit pas', synR.msg, ei + 1 + synR.index);
  }
  if (!synR.complete) return incomplete();
  const Ls = splitTop(left, '=').map(exprOf);
  const Rs = splitTop(right, '=').map(exprOf);
  if ([...Ls, ...Rs].some((m) => !m))
    return syntax('Ça ne se lit pas', 'Une expression ne se lit pas : vérifie les parenthèses.');
  if ([...Ls, ...Rs].some((m) => hasO(m!)))
    return syntax('Sans reste', 'Un équivalent s’écrit sans o(…) : « expression ∼ x² ÷ 2 ».');
  void opts;
  return judgeEquiv(ks, { atoms: A, line: lineOf(A) }, Ls as Term[], Rs as Term[]);
}

/** « A = … ∼ B = … » : les égalités, puis l'équivalent de la dernière écriture de gauche */
function judgeEquiv(ks: KLimState, base: Base, L: Term[], R: Term[]): KLimVerdict {
  const text = klimText(base.atoms);
  if (ks.givenTexts.has(text))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà dans l’énoncé.',
    };
  const P = ks.P;
  const view = viewOf(ks);
  const move = emptyMove(text);
  move.equivs = [];
  const steps: StepKind[] = [];
  [...L, ...R].forEach((m) => move.terms.push([keyOf(m), m]));
  for (let i = 1; i < L.length; i++) {
    const r = judgeRewrite(ks, view, L[i - 1]!, L[i]!);
    if (r.k === 'false') return trapOr(ks, base, { title: r.title, message: r.message });
    if (r.k === 'step') steps.push(...r.steps);
    union(view, keyOf(L[i - 1]!), keyOf(L[i]!));
    move.eqs.push([keyOf(L[i - 1]!), keyOf(L[i]!)]);
  }
  for (let i = 1; i < R.length; i++) {
    if (!sameNear(R[i - 1]!, R[i]!, P))
      return trapOr(ks, base, {
        title: 'Pas la même expression',
        message: 'Après « ∼ », les écritures reliées par « = » doivent être égales.',
      });
    union(view, keyOf(R[i - 1]!), keyOf(R[i]!));
    move.eqs.push([keyOf(R[i - 1]!), keyOf(R[i]!)]);
  }
  const C = L[L.length - 1]!;
  const B = R[0]!;
  if (isZeroConst(B, P))
    return trapOr(ks, base, {
      title: 'Rien n’est équivalent à 0',
      message:
        'Seule une fonction nulle est équivalente à 0 : écris un équivalent qui n’est pas 0.',
    });
  if (sameNear(C, B, P))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est une égalité',
      message: 'Les deux écritures sont égales : écris « = ». Un équivalent, lui, simplifie.',
      lmove: move,
    };
  const ratio = equivRatio(C, B, P);
  if (!ratio)
    return {
      kind: 'useless',
      ...base,
      unknown: true,
      title: 'Je ne sais pas encore vérifier ce passage',
      message: 'Ta ligne reste sur la feuille, sans HORA.',
      lmove: move,
    };
  if (!(ratio.k === 'fin' && Math.abs(ratio.v - 1) < 1e-9)) {
    const coefOff = ratio.k === 'fin' && Math.abs(ratio.v) > 1e-9;
    return trapOr(
      ks,
      base,
      coefOff
        ? {
            title: 'Le coefficient ne va pas',
            message: 'Le quotient des deux ne tend pas vers 1 : vérifie le coefficient.',
          }
        : {
            title: 'Pas le même ordre de grandeur',
            message:
              'Le quotient des deux ne tend pas vers 1 : vérifie la puissance, le terme qui l’emporte.',
          },
    );
  }
  const before = equivClasses(view, ks.equivs, keyOf(C));
  const knownEq = (r: Term) =>
    before.roots.has(find(view, keyOf(r))) || before.terms.some((t) => sameNear(t, r, P));
  R.forEach((r) => move.equivs!.push([keyOf(C), r]));
  const final = !ks.concluded && equivReachesAnswer(ks, view, [...ks.equivs, ...move.equivs]);
  if (R.every(knownEq) && !steps.length)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cet équivalent est déjà écrit.',
      lmove: move,
      final,
    };
  const pairs = equivDiff(C, B, P.x);
  let jump = !pairs.length || pairs.length > 2;
  const eqSteps: EquivStep[] = [];
  if (!jump)
    for (const [sT, rT] of pairs) {
      if (!equivHolds(sT, rT, P)) {
        jump = true;
        break;
      }
      const k = classifyEquiv(sT, rT, P, sT === C, (t) => dlOf(view, keyOf(t)));
      if (!k) {
        jump = true;
        break;
      }
      eqSteps.push(k);
    }
  if (jump)
    return jumpVerdict(
      base,
      move,
      final,
      final
        ? `C’est le bon équivalent. Justifie-le : ${EQUIV_JUMP} Tu peux garder cette ligne sans HORA.`
        : `Ta ligne est juste. Justifie-la : ${EQUIV_JUMP} Tu peux la garder sans HORA.`,
    );
  steps.push(...eqSteps);
  return stepVerdict(base, steps, move, final);
}

/* ------------------------------ développements limités (v58) ------------------------------ */

/** la limite lue sur un développement correspond-elle à la limite écrite ? */
function limMatches(val: LimVal, L: { k: 'fin'; v: number } | { k: 'inf'; s: 1 | -1 }): boolean {
  if (L.k === 'inf') return val.k === 'inf' && val.s === L.s;
  return val.k === 'fin' && Math.abs(val.v - L.v) < 1e-9 * Math.max(1, Math.abs(L.v));
}

type DLPart =
  | { k: 'step'; steps: DLStep[]; serY: Ser }
  | { k: 'none'; serY: Ser; known: boolean }
  | { k: 'jump'; message: string; serY: Ser }
  | { k: 'unknown' }
  | { k: 'false'; title: string; message: string };

const DL_JUMP =
  'Ton développement est juste, mais tu enchaînes plusieurs calculs d’un coup : écris-les un par un pour gagner des HORA. Tu peux garder cette ligne sans HORA.';

/** X = Y, où Y écrit un reste o(…) : vrai ? et quels gestes ? */
function dlPair(ks: KLimState, v: View, X: Term, Y: Term): DLPart {
  const x = ks.P.x;
  const N = Math.max(maxOOrder(Y), maxOOrder(X), 0) + 8;
  const SY = seriesOf(Y, x, N);
  if (!SY) return { k: 'unknown' };
  const SX = hasO(X) ? seriesOf(X, x, N) : seriesTo(X, x, SY.e);
  if (!SX) return { k: 'unknown' };
  const bad = agreeTo(SX, SY, Math.min(SX.e, SY.e));
  if (bad !== null) {
    const name = degreeName(bad, x);
    return {
      k: 'false',
      title: 'Un coefficient ne va pas',
      message: `${name[0]!.toUpperCase()}${name.slice(1)} ne va pas : reprends ce calcul.`,
    };
  }
  if (SY.e > SX.e)
    return {
      k: 'false',
      title: 'Trop précis',
      message: `L’écriture de gauche n’est connue qu’à ${oText(SX.e, x)} près : tu ne peux pas écrire ${oText(SY.e, x)}.`,
    };
  // déjà établi, aussi précis : rien de nouveau
  if (!hasO(X)) {
    const k = dlOf(v, keyOf(X));
    if (k && k.e >= SY.e) return { k: 'none', serY: SY, known: true };
  }
  const lookup = (t: Term): Ser | null => (hasO(t) ? null : dlOf(v, keyOf(t)));
  let dX = null;
  for (const fresh of [false, true]) {
    for (let n = Math.max(SY.e, 0) + 2; n <= Math.max(SY.e, 0) + 12; n += 2) {
      const d = dlDerive(X, { x, N: n, lookup, fresh });
      if (d && d.ser.e >= SY.e) {
        dX = d;
        break;
      }
    }
    if (dX) break;
  }
  if (!dX) return { k: 'jump', message: DL_JUMP, serY: SY };
  const dY = dlDerive(Y, { x, N, lookup, fresh: false });
  const used = stepsDiff(dX.steps, dY?.steps ?? []);
  if (used.length > 3) return { k: 'jump', message: DL_JUMP, serY: SY };
  if (!used.length) return { k: 'none', serY: SY, known: false };
  return { k: 'step', steps: used, serY: SY };
}

/**
 * Une ligne qui écrit des développements : « e^x − 1 − x = x² ÷ 2 + o(x²) »,
 * « (sin(x) − x) ÷ (x × sin(x)) = (−x³ ÷ 6 + o(x³)) ÷ (x² + o(x²)) = −x ÷ 6 + o(x) ».
 * Elle commence par une expression exacte ; un reste ne disparaît jamais d'un « = ».
 */
function judgeDLChain(
  ks: KLimState,
  base: Base,
  members: Term[],
  value: LimVal | null,
): KLimVerdict {
  const text = klimText(base.atoms);
  const x = ks.P.x;
  if (ks.P.inf || Math.abs(ks.P.a) > 1e-12)
    return {
      kind: 'syntax',
      ...base,
      title: 'En 0 seulement',
      message: 'Un développement limité s’écrit ici quand x tend vers 0.',
    };
  if (hasO(members[0]!))
    return {
      kind: 'syntax',
      ...base,
      title: 'L’expression d’abord',
      message: 'Commence par l’expression, puis son développement : « eˣ = 1 + x + o(x) ».',
    };
  for (const m of members) {
    const b = beyondOrder(m, x);
    if (b)
      return {
        kind: 'syntax',
        ...base,
        title: `Au-delà, c’est ${oText(b.k, x)}`,
        message: `${degreeName(b.d, x)[0]!.toUpperCase()}${degreeName(b.d, x).slice(1)} est déjà dans ${oText(b.k, x)} : arrête ton développement à l’ordre ${b.k}.`,
      };
  }
  const view = viewOf(ks);
  const move = emptyMove(text);
  const steps: StepKind[] = [];
  let jump: string | null = null;
  let unknown = false;
  let known = false;
  let firstO: Ser | null = null;
  let lastO: Ser | null = null;
  let lastExact = members[0]!;
  members.forEach((m) => {
    if (!hasO(m)) move.terms.push([keyOf(m), m]);
  });
  for (let i = 1; i < members.length; i++) {
    const X = members[i - 1]!;
    const Y = members[i]!;
    if (!hasO(X) && !hasO(Y)) {
      const r = judgeRewrite(ks, view, X, Y);
      if (r.k === 'false') return trapOr(ks, base, { title: r.title, message: r.message });
      if (r.k === 'step') steps.push(...r.steps);
      union(view, keyOf(X), keyOf(Y));
      move.eqs.push([keyOf(X), keyOf(Y)]);
      lastExact = Y;
      continue;
    }
    if (!hasO(Y))
      return trapOr(ks, base, {
        title: 'Le reste ne disparaît pas',
        message:
          'Un o(…) ne disparaît pas d’un « = » : garde-le, ou écris la limite avec « → » sur une autre ligne.',
      });
    const r = dlPair(ks, view, X, Y);
    if (r.k === 'false') return trapOr(ks, base, { title: r.title, message: r.message });
    if (r.k === 'unknown') {
      unknown = true;
      continue;
    }
    if (r.k === 'jump') jump = r.message;
    if (r.k === 'step') steps.push(...r.steps);
    if (r.k === 'none' && r.known) known = true;
    if (!firstO) firstO = r.serY;
    lastO = r.serY;
  }
  if (firstO && !unknown) {
    move.dls.push([keyOf(lastExact), firstO]);
    setDL(view, keyOf(lastExact), firstO);
  }
  if (value) {
    const M = members[members.length - 1]!;
    if (hasO(M)) {
      const L = lastO ? serLimit(lastO, ks.P.side) : null;
      if (!L)
        return trapOr(ks, base, {
          title: 'Pas assez précis',
          message: 'Avec ce reste, on ne lit pas la limite : il faut au moins un o(1).',
        });
      if (!limMatches(value, L))
        return trapOr(ks, base, {
          title: 'Ce n’est pas la limite',
          message: 'Lis la limite sur le développement : o(1) tend vers 0.',
        });
      steps.push('dlLimit');
      const keep = L.k === 'fin' ? fin(L.v) : ({ k: 'inf', s: L.s } as LimVal);
      move.lims.push([keyOf(lastExact), keep]);
      view.lim.set(find(view, keyOf(lastExact)), keep);
    } else {
      const r = judgeLimit(ks, view, M, value);
      if (r.k === 'false') return trapOr(ks, base, { title: r.title, message: r.message });
      if (r.k === 'step') steps.push(...r.steps);
      if (r.k === 'jump') jump = r.message ?? DL_JUMP;
      if (r.k === 'unknown') unknown = true;
      if (r.k !== 'unknown') {
        move.lims.push([keyOf(M), value]);
        view.lim.set(find(view, keyOf(M)), value);
      }
    }
  }
  const final = ks.equiv
    ? !ks.concluded && equivReachesAnswer(ks, view, ks.equivs)
    : !!view.lim.get(find(view, ks.targetKey)) && !ks.lim.get(find(ks, ks.targetKey));
  if (unknown)
    return {
      kind: 'useless',
      ...base,
      unknown: true,
      title: 'Je ne sais pas encore vérifier ce passage',
      message: 'Ta ligne reste sur la feuille, sans HORA.',
      lmove: move,
    };
  if (jump !== null) return jumpVerdict(base, move, final, jump);
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: known ? 'Rien de nouveau' : 'Juste, continue',
      message: known
        ? 'Ce développement est déjà écrit.'
        : 'Ta ligne est juste : continue le calcul (réduire, diviser…) pour gagner des HORA.',
      lmove: move,
      final,
    };
  return stepVerdict(base, steps, move, final);
}

/** une ligne de développements : vraie ? (pour la porte et les pièges) */
function dlLineHolds(ks: KLimState, members: Term[], value: LimVal | null): boolean {
  const x = ks.P.x;
  if (hasO(members[0]!)) return false;
  let last: Ser | null = null;
  for (let i = 1; i < members.length; i++) {
    const X = members[i - 1]!;
    const Y = members[i]!;
    if (!hasO(X) && !hasO(Y)) {
      if (!sameNear(X, Y, ks.P)) return false;
      continue;
    }
    if (!hasO(Y)) return false;
    const N = Math.max(maxOOrder(Y), maxOOrder(X), 0) + 8;
    const SY = seriesOf(Y, x, N);
    if (!SY) return false;
    const SX = hasO(X) ? seriesOf(X, x, N) : seriesTo(X, x, SY.e);
    if (!SX || SY.e > SX.e || agreeTo(SX, SY, SY.e) !== null) return false;
    last = SY;
  }
  if (value) {
    const M = members[members.length - 1]!;
    if (hasO(M)) {
      const L = last ? serLimit(last, ks.P.side) : null;
      return !!L && limMatches(value, L);
    }
    const t = trueLimit(M, ks.P).v;
    return !!t && sameLimit(value, t);
  }
  return members.length > 1;
}

/* ------------------------------ encadrements ------------------------------ */

function judgeBound(ks: KLimState, base: Base, left: string[]): KLimVerdict {
  const text = klimText(base.atoms);
  const parts: string[][] = [[]];
  const rels: string[] = [];
  let depth = 0;
  for (const a of left) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && CMP.has(a)) {
      rels.push(a);
      parts.push([]);
    } else parts[parts.length - 1]!.push(a);
  }
  const terms = parts.map(exprOf);
  if (terms.some((t) => !t) || rels.length > 2)
    return {
      kind: 'syntax',
      ...base,
      title: 'Ça ne se lit pas',
      message: 'Écris « a ≤ u ≤ b » ou « u ≥ a ».',
    };
  const T = terms as Term[];
  const up = rels.every((r) => r === '<' || r === '≤');
  const down = rels.every((r) => r === '>' || r === '≥');
  if (!up && !down)
    return {
      kind: 'syntax',
      ...base,
      title: 'Un seul sens',
      message: 'Un encadrement va dans un seul sens : « a ≤ u ≤ b ».',
    };
  for (let i = 0; i < rels.length; i++) {
    const h = holdsNear(T[i]!, rels[i] as '≤', T[i + 1]!, ks.P);
    if (h === false)
      return trapOr(ks, base, {
        title: 'Inégalité fausse',
        message: 'Cette inégalité n’est pas vraie près du point : vérifie-la.',
      });
    if (h === null)
      return {
        kind: 'useless',
        ...base,
        unknown: true,
        title: 'Je ne sais pas encore vérifier ce passage',
        message: 'Ta ligne reste sur la feuille, sans HORA.',
      };
  }
  const move = emptyMove(text);
  T.forEach((t) => move.terms.push([keyOf(t), t]));
  // u au milieu (ou à gauche) ; ses bornes
  const lowOf = (i: number) => (up ? T[i]! : T[i + 1]!);
  const highOf = (i: number) => (up ? T[i + 1]! : T[i]!);
  let mid: Term;
  if (T.length === 3) {
    mid = T[1]!;
    move.lower.push([keyOf(mid), up ? T[0]! : T[2]!]);
    move.upper.push([keyOf(mid), up ? T[2]! : T[0]!]);
  } else {
    mid = T[0]!;
    if (up) move.upper.push([keyOf(mid), highOf(0)]);
    else move.lower.push([keyOf(mid), lowOf(0)]);
  }
  const root = find(ks, keyOf(mid));
  const known = ks.lim.get(root);
  if (known || !hasVar(mid, ks.P.x))
    return {
      kind: 'useless',
      ...base,
      title: 'Juste, mais à côté',
      message: 'C’est juste, mais ça ne sert pas pour la question.',
      lmove: move,
    };
  const same = (a: Term[] | undefined, t: Term) => (a ?? []).some((u) => keyOf(u) === keyOf(t));
  if (
    move.lower.every(([k, t]) => same(ks.lower.get(find(ks, k)), t)) &&
    move.upper.every(([k, t]) => same(ks.upper.get(find(ks, k)), t))
  )
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cet encadrement est déjà écrit.',
      lmove: move,
    };
  return stepVerdict(base, ['bound'], move, false);
}

/* ------------------------------ mémoire ------------------------------ */

export function klimCommit(ks: KLimState, v: KLimVerdict): void {
  const m = v.lmove;
  if (!m) return;
  ks.written.add(m.text);
  ks.lines++;
  m.terms.forEach(([k, t]) => {
    if (!ks.terms.has(k)) ks.terms.set(k, t);
  });
  const view: View = {
    parent: ks.parent,
    lim: ks.lim,
    lower: ks.lower,
    upper: ks.upper,
    dls: ks.dls,
  };
  m.eqs.forEach(([a, b]) => union(view, a, b));
  m.lims.forEach(([k, l]) => {
    const r = find(ks, k);
    if (!ks.lim.has(r)) ks.lim.set(r, l);
  });
  (m.dls ?? []).forEach(([k, s]) => setDL(ks, k, s));
  m.lower.forEach(([k, t]) => {
    const r = find(ks, k);
    ks.lower.set(r, [...(ks.lower.get(r) ?? []), t]);
  });
  m.upper.forEach(([k, t]) => {
    const r = find(ks, k);
    ks.upper.set(r, [...(ks.upper.get(r) ?? []), t]);
  });
  (m.equivs ?? []).forEach((e) => ks.equivs.push(e));
  if (ks.equiv) {
    if (equivReachesAnswer(ks, ks, ks.equivs)) ks.concluded = true;
  } else if (ks.lim.get(find(ks, ks.targetKey))) ks.concluded = true;
}

export function klimGoalReached(ks: KLimState): boolean {
  return ks.concluded;
}

interface ParsedLine {
  members: Term[];
  value: LimVal | null;
  bound: { terms: Term[]; rels: string[] } | null;
  /** v61 : « A ∼ B = … » : les écritures après « ∼ » */
  equiv?: Term[];
}

function parseLineText(text: string): ParsedLine | null {
  let A: string[];
  try {
    A = klimLineAtoms(text);
  } catch {
    return null;
  }
  const ei = A.indexOf(EQUIV);
  if (ei >= 0) {
    const L = splitTop(A.slice(0, ei), '=').map(exprOf);
    const R = splitTop(A.slice(ei + 1), '=').map(exprOf);
    if (!L.length || !R.length || [...L, ...R].some((m) => !m)) return null;
    return { members: L as Term[], value: null, bound: null, equiv: R as Term[] };
  }
  const ai = A.indexOf(ARROW);
  const left = ai < 0 ? A : A.slice(0, ai);
  if (left.some((a) => CMP.has(a))) {
    const parts: string[][] = [[]];
    const rels: string[] = [];
    let depth = 0;
    for (const a of left) {
      if (a === '(') depth++;
      if (a === ')') depth--;
      if (depth === 0 && CMP.has(a)) {
        rels.push(a);
        parts.push([]);
      } else parts[parts.length - 1]!.push(a);
    }
    const terms = parts.map(exprOf);
    if (terms.some((t) => !t)) return null;
    return { members: [], value: null, bound: { terms: terms as Term[], rels } };
  }
  const members = splitTop(left, '=').map(exprOf);
  if (members.some((m) => !m)) return null;
  const value = ai >= 0 ? valueOf(A.slice(ai + 1)) : null;
  if (ai >= 0 && !value) return null;
  return { members: members as Term[], value, bound: null };
}

/** la ligne est vraie */
export function klimHolds(ks: KLimState, text: string): boolean {
  const p = parseLineText(text);
  if (!p) return false;
  if (p.equiv) {
    const all = [...p.members, ...p.equiv];
    if (all.some(hasO)) return false;
    for (const side of [p.members, p.equiv])
      for (let i = 1; i < side.length; i++)
        if (!sameNear(side[i - 1]!, side[i]!, ks.P)) return false;
    return equivHolds(p.members[p.members.length - 1]!, p.equiv[0]!, ks.P);
  }
  if (p.bound) {
    const { terms, rels } = p.bound;
    return rels.every((r, i) => holdsNear(terms[i]!, r as '≤', terms[i + 1]!, ks.P) === true);
  }
  if (p.members.some(hasO)) return ks.dl && dlLineHolds(ks, p.members, p.value);
  for (let i = 1; i < p.members.length; i++)
    if (!sameNear(p.members[i - 1]!, p.members[i]!, ks.P)) return false;
  if (p.value) {
    const t = trueLimit(p.members[p.members.length - 1]!, ks.P).v;
    return !!t && sameLimit(p.value, t);
  }
  return p.members.length > 1;
}

/** la ligne donne la limite de l'expression de la question */
export function klimIsAnswer(ks: KLimState, text: string): boolean {
  const p = parseLineText(text);
  // v61 : un équivalent de l'expression de la question
  if (ks.equiv) {
    if (!p || !p.equiv) return false;
    if (
      !p.members.some(
        (m) => keyOf(m) === ks.targetKey || find(ks, keyOf(m)) === find(ks, ks.targetKey),
      )
    )
      return false;
    return klimHolds(ks, text);
  }
  if (!p || !p.value || p.bound || p.equiv || p.members.some(hasO)) return false;
  if (
    !p.members.some(
      (m) => keyOf(m) === ks.targetKey || find(ks, keyOf(m)) === find(ks, ks.targetKey),
    )
  )
    return false;
  return (
    sameLimit(p.value, ks.answer) ||
    (p.value.k === 'fin' &&
      !p.value.side &&
      sameLimit(p.value, { ...ks.answer, ...(ks.answer.k === 'fin' ? { side: 0 } : {}) } as LimVal))
  );
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function klimEstablished(ks: KLimState, text: string): boolean {
  const p = parseLineText(text);
  if (!p) return false;
  if (ks.written.has(klimText(klimLineAtoms(text)))) return true;
  if (p.equiv) {
    for (let i = 1; i < p.members.length; i++)
      if (find(ks, keyOf(p.members[i - 1]!)) !== find(ks, keyOf(p.members[i]!))) return false;
    const c = equivClasses(ks, ks.equivs, keyOf(p.members[p.members.length - 1]!));
    return p.equiv.every(
      (r) => c.roots.has(find(ks, keyOf(r))) || c.terms.some((t) => sameNear(t, r, ks.P)),
    );
  }
  if (p.bound) {
    const T = p.bound.terms;
    const mid = T.length === 3 ? T[1]! : T[0]!;
    const r = find(ks, keyOf(mid));
    return !!ks.lower.get(r)?.length || !!ks.upper.get(r)?.length;
  }
  if (p.members.some(hasO)) {
    // un développement : établi s'il est connu aussi précis pour l'expression
    const exact = p.members.filter((m) => !hasO(m));
    const last = exact[exact.length - 1]!;
    for (let i = 1; i < exact.length; i++)
      if (find(ks, keyOf(exact[i - 1]!)) !== find(ks, keyOf(exact[i]!))) return false;
    const firstO = p.members.find(hasO)!;
    const s = seriesOf(firstO, ks.P.x, Math.max(maxOOrder(firstO), 0) + 8);
    const k = ks.dls.get(find(ks, keyOf(last)));
    if (!s || !k || k.e < s.e) return false;
    if (p.value) return !!ks.lim.get(find(ks, keyOf(last)));
    return true;
  }
  for (let i = 1; i < p.members.length; i++)
    if (find(ks, keyOf(p.members[i - 1]!)) !== find(ks, keyOf(p.members[i]!))) return false;
  if (p.value) {
    const l = ks.lim.get(find(ks, keyOf(p.members[p.members.length - 1]!)));
    return !!l && (sameLimit(p.value, l) || sameLimit(l, p.value));
  }
  return true;
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la première ligne du corrigé pas encore écrite */
export function klimNextMove(
  ks: KLimState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  for (const t of ks.path) {
    if (klimEstablished(ks, t)) continue;
    const atoms = klimLineAtoms(t);
    const v = klimJudge(ks, chipsOf(atoms), { verify: true });
    return { line: klimLine(t), text: klimText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function klimSolvePath(
  model: ScratchModel,
  opts?: { v54?: boolean },
): {
  lines: { line: Line; text: string; atoms: string[]; v: KLimVerdict }[];
  reached: boolean;
} {
  const ks = createKLimState(model, opts);
  const lines: { line: Line; text: string; atoms: string[]; v: KLimVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = klimLineAtoms(t);
    const v = klimJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    klimCommit(ks, v);
    lines.push({ line: klimLine(t), text: klimText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}

/* ------------------------------ version ------------------------------ */

const ARC_TEXT_RE = /arc(?:tan|sin|cos)/;
const needsV56Cache = new WeakMap<object, boolean>();

/**
 * La feuille a-t-elle besoin du moteur v56 ? Oui si elle écrit arctan (radians), ou si
 * son corrigé ne se rejoue pas à l'identique avec le moteur v54 (composée, uᵛ = e^(v ln u),
 * sin(1 ÷ x) borné…).
 */
export function klimNeedsV56(model: ScratchModel): boolean {
  const hit = needsV56Cache.get(model);
  if (hit !== undefined) return hit;
  let out = false;
  const spec = model.kernel;
  if (spec) {
    const texts = [spec.start, spec.answer, ...(spec.path ?? [])];
    if (texts.some((t) => ARC_TEXT_RE.test(t))) out = true;
    else
      try {
        const now = klimSolvePath(model);
        const old = klimSolvePath(model, { v54: true });
        const sig = (p: typeof now) =>
          JSON.stringify([
            p.reached,
            p.lines.map((l) => [l.text, l.v.kind, l.v.hora ?? 0, l.v.rule?.id ?? '']),
          ]);
        out = sig(now) !== sig(old);
      } catch {
        out = false;
      }
  }
  needsV56Cache.set(model, out);
  return out;
}

/* ------------------------------ palette ------------------------------ */

/** les opérations des limites, dans l'ordre d'affichage */
export const LIMIT_OPS = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  '²',
  '^',
  '√',
  'exp',
  'ln',
  'cos',
  'sin',
  'arctan',
  '|',
  '≤',
  '≥',
  '=',
];

/**
 * Les bouts (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) : la variable,
 * les nombres, les opérations utiles, « → » et les limites (« +∞ », « −∞ », « 0⁺ », « 0⁻ »).
 */
export function klimPaletteParts(model: ScratchModel): {
  names: string[];
  numbers: string[];
  ops: string[];
  limits: string[];
  /** v58 : les restes « o(1) », « o(x²) »… du corrigé */
  dl: string[];
} {
  const spec = model.kernel!;
  const texts = [spec.start, ...(spec.path ?? []), spec.answer];
  const atoms = texts.flatMap((t) => {
    try {
      return klimLineAtoms(t);
    } catch {
      return [];
    }
  });
  const x = spec.goal.target;
  const names = [x, ...(atoms.includes('e') ? ['e'] : []), ...(atoms.includes('π') ? ['π'] : [])];
  const nums = [...new Set(atoms.filter((a) => /^\d+(,\d+)?$/.test(a)))];
  const val = (s: string) => Number(s.replace(',', '.'));
  nums.sort((a, b) => val(a) - val(b) || a.localeCompare(b));
  const used = new Set(atoms);
  const always = new Set(['+', '−', '×', '÷', '(', ')', '=']);
  const ops = LIMIT_OPS.filter((o) => always.has(o) || used.has(o));
  const sups = [...new Set(atoms.filter((a) => /^[⁰¹³⁴⁵⁶⁷⁸⁹]+$/.test(a)))];
  if (sups.length)
    ops.splice(ops.indexOf('²') >= 0 ? ops.indexOf('²') + 1 : ops.indexOf('='), 0, ...sups);
  // un côté écrit dans le corrigé (« 0⁻ ») : l'autre côté aussi, c'est à l'élève de choisir
  const sides = atoms
    .filter((a) => /^\d+(,\d+)?[⁺⁻]$/.test(a))
    .flatMap((a) => [a.slice(0, -1) + '⁺', a.slice(0, -1) + '⁻']);
  const limits = ['+∞', '−∞', ...new Set(sides)];
  const dl = [...new Set(atoms.filter((a) => oAtomOrder(a) !== null))].sort(
    (a, b) => oAtomOrder(a)! - oAtomOrder(b)!,
  );
  // v61 : « ∼ » quand la feuille écrit des équivalents
  const rel = atoms.includes(EQUIV) ? [ARROW, EQUIV] : [ARROW];
  return { names, numbers: nums, ops: [...ops, ...rel], limits, dl };
}

export { limValText, isCmp };
